// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { SurveyType } from '../../api/types';
import type { CaseActions, CaseData } from '../../app/bygning/useCaseData';
import { kindOf } from '../../app/keyTargets';
import { createOnceGuard } from '../../app/onceGuard';
import { lensToken } from '../../bygning/lens';
import { partMassText } from '../../bygning/measure';
import { activePart, ownerOf, partCountText } from '../../bygning/parts';
import { isInstanceBacked, isManualType } from '../../bygning/provenance';
import {
  activeFilterCount,
  countText,
  filterResources,
  massText,
  matchType,
  roomFigure,
  type FilteredResources,
  type PartsFigure,
  type ResourceFilter,
} from '../../bygning/resourceFilter';
import { stripStatus } from '../../bygning/review';
import { roomName, roomsOf, type RoomInfo } from '../../bygning/rooms';
import { NO_FILTER_PATCH } from '../../bygning/selection';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Pill } from '../../components/Pill';
import { TextLink } from '../../components/TextLink';
import { ToggleChip } from '../../components/ToggleChip';
import { PartBar } from '../../components/bygning/PartBar';
import { ResourceFilters } from '../../components/bygning/ResourceFilters';
import { Strip } from '../../components/bygning/Strip';
import { StripCard } from '../../components/bygning/StripCard';
import { TableViewSwitch } from '../../components/bygning/TableViewSwitch';
import { EvidenceTile } from '../../components/bygning/evidence/EvidenceTile';
import { GalleriTile } from '../../components/bygning/galleri/GalleriTile';
import { PartInspector } from '../../components/bygning/inspector/PartInspector';
import { TypeInspector } from '../../components/bygning/inspector/TypeInspector';
import { useReview } from '../../components/bygning/inspector/useReview';
import { useGroundSettings, useStoreys } from '../../components/bygning/settings/SettingsContext';
import { TabelTile } from '../../components/bygning/tabel/TabelTile';
import { coverageParts } from '../../kortlaegning/copy';
import { partLabel } from '../../kortlaegning/model';
import { formatQuantity, formatTonnes } from '../../kortlaegning/vocab';
import type { ModeComponent, ModeProps, ModeView } from './modeTypes';
import styles from './RessourcerMode.module.css';

/** The name a type made without geometry starts with (renamed straight away). */
const MANUAL_TYPE_NAME = 'Ny type (uden geometri)';

/**
 * "+ Tilføj uden geometri" — the strip's dashed card and the ⌘K command
 * "add-manual": a type with one manual part, placed in `room` (the focused
 * room's name) when there is one. Resolves to the new type, or null when
 * the create failed (its toast is the case store's).
 */
export async function addManualType(actions: CaseActions, room: string | null): Promise<SurveyType | null> {
  const created = await actions.createType({ name: MANUAL_TYPE_NAME, unit: 'm²', treatment: 'bortskaffelse' });
  if (!created) return null;
  const part = await actions.createResource({ type_id: created.id, name: MANUAL_TYPE_NAME });
  if (part && room) await actions.patchPart(part.code, { room_name: room });
  return created;
}

/** "Ingen kortlægning endnu" with "Opret kortlægning fra instanser" (`POST /survey/sync`), as the old page. */
export function NoSurvey({ data, actions }: { data: CaseData; actions: CaseActions }) {
  return (
    <div className={styles.empty}>
      <EmptyState
        bare
        title="Ingen kortlægning endnu"
        detail="Opret typer og bygningsdele ud fra projektets instanser. Kræver at rux create instances er kørt."
        action={
          data.canEdit ? (
            <Button variant="primary" size="lg" onClick={() => void actions.syncSurvey()} disabled={data.busy}>
              Opret kortlægning fra instanser
            </Button>
          ) : undefined
        }
      />
    </div>
  );
}

/**
 * One type in a strip (v8 `.cd`): swatch, name, tonnes, EAK/id and its status
 * pill. Under a room focus (`figure`, `roomFigure`) the tonnes and the footer
 * are the room's share: `≈ 5,2 t`, `3 dele · 26,9 m²`.
 */
export function TypeCard({
  type,
  swatch,
  active,
  onSelect,
  figure,
}: {
  type: SurveyType;
  swatch: string | null;
  active: boolean;
  onSelect: () => void;
  figure?: PartsFigure;
}) {
  const status = stripStatus(type);
  const mass = figure ? figure.mass : type.mass_t;
  return (
    <StripCard
      variant="type"
      title={type.name}
      swatch={swatch}
      figure={mass === null ? 'tons mangler' : figure ? massText(figure) : formatTonnes(mass)}
      figureMuted={mass === null}
      meta={figure ? `${partCountText(figure.parts)} · ${formatQuantity(figure.quantity, type.unit)}` : type.eak_code || `#${type.id}`}
      status={<Pill tone={status.tone}>{status.label}</Pill>}
      active={active}
      manual={isManualType(type)}
      onClick={onSelect}
    />
  );
}

/**
 * The inspector's own keys, beside the shell's J/K/G/A/Esc: V toggles ★ (the
 * focused part's, else the type's) and S opens Del op for the active part.
 * Like the shell's, they never fire while typing or with a modifier.
 */
export function useInspectorKeys(props: ModeProps, type: SurveyType | null) {
  const latest = useRef({ props, type });
  latest.current = { props, type };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return;
      const kind = kindOf(e.target);
      if (kind === 'text' || kind === 'choice') return;
      const { props: p, type: t } = latest.current;
      if (!t || !p.data.canEdit) return;
      const key = e.key.toLowerCase();
      if (key === 'v') {
        e.preventDefault();
        const part = p.sel.partCode !== null ? t.parts.find((x) => x.code === p.sel.partCode) : undefined;
        if (part) void p.actions.patchPart(part.code, { starred: !part.starred });
        else void p.actions.patchType(t.id, { starred: !t.starred });
      } else if (key === 's') {
        const code = activePart(t, p.sel.partCode, p.sel.selParts);
        if (!code) return;
        e.preventDefault();
        p.setSel({ partCode: code, ev: 'seg' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

/** The evidence tile for the selection, or null (`?ev=` with a part of the type). */
export function evidenceCentre(props: ModeProps, type: SurveyType | null) {
  const { sel, setSel } = props;
  if (!sel.ev || !type) return null;
  const code = sel.partCode ?? activePart(type, null, sel.selParts);
  if (!code) return null;
  return (
    <EvidenceTile
      partCode={code}
      kind={sel.ev}
      onClose={() => setSel({ ev: null })}
      onKind={(kind) => setSel({ partCode: code, ev: kind })}
      onFocusRoom={(room) => setSel({ room })}
    />
  );
}

/**
 * Ressourcer (= Kortlægning): the shared filters (`ResourceFilters`, all in
 * the URL) and the coverage notice on the left (the lens legend floats over
 * the model: the Ground draws it), the inspector on the right — the type's,
 * or, while `?part=` names one of its parts (a Dele chip, Tabel › Dele,
 * Galleri, the Dele strip), the part's own (`PartInspector`, a drill-down:
 * "← {type}", Esc or the strip go back; a part not in the type falls back to
 * the type's) — the strip at the bottom — the filtered set's types (plus "+ Tilføj uden
 * geometri"), or under `?tview=dele` its parts — and in the centre either
 * the evidence tile (`?ev=`), Tabel (`?view=tabel`) or Galleri
 * (`?view=galleri`, the types as image cards) — both list the same set
 * themselves, so they drop the strip. The PartBar floats over everything
 * while parts are picked; it is returned in `bottom`, or with Tabel/Galleri
 * in `centre` (it renders through a portal, so the slot only decides when it
 * exists).
 */
export const RessourcerMode: ModeComponent = (props) => {
  const { data, sel, setSel, actions } = props;
  const [renameId, setRenameId] = useState<number | null>(null);
  const createGuard = useRef(createOnceGuard());
  // The one filtered set Model, Tabel and Galleri show (the shell's J/K walks the same).
  const filter = useMemo<ResourceFilter>(
    () => ({ room: sel.room, search: sel.search, env: sel.env, starred: sel.starred, errors: sel.errors }),
    [sel.room, sel.search, sel.env, sel.starred, sel.errors],
  );
  const filtered = useMemo(() => filterResources(data.types, data.dismissed, filter), [data.types, data.dismissed, filter]);
  const stripList = useMemo(() => filtered.types.map((m) => m.type), [filtered.types]);
  const inStrip = useCallback((t: SurveyType) => matchType(t, filter) !== null, [filter]);
  const onReview = useReview(data, actions, setSel, inStrip);
  const filtersOn = activeFilterCount(filter) > 0;

  // A selected type outside the filters stays selected (it is just not in the strip); a bare `?part=` finds its owner.
  const type =
    data.types.find((t) => t.id === sel.typeId) ??
    (sel.typeId === null && sel.partCode !== null ? ownerOf(data.types, sel.partCode) : null) ??
    stripList[0] ??
    null;
  // The part drilled into (the right tile shows its own inspector); null when `?part=` is not the type's.
  const part = type && sel.partCode !== null ? (type.parts.find((p) => p.code === sel.partCode) ?? null) : null;
  const partCode = part?.code ?? null;
  // The part last drilled into: back on the type, its chip's room reopens (PartChips `recent`). Drilling
  // down also ends a pending rename, or the type inspector, remounted on the way back, would reopen it.
  const [lastPart, setLastPart] = useState<string | null>(null);
  useEffect(() => {
    if (partCode === null) return;
    setLastPart(partCode);
    setRenameId(null);
  }, [partCode]);
  useInspectorKeys(props, type);
  // The rename prompt belongs to the type just made; leaving it ends it.
  const typeId = type?.id ?? null;
  useEffect(() => {
    if (renameId !== null && typeId !== null && typeId !== renameId) setRenameId(null);
  }, [typeId, renameId]);

  const empty = data.loaded && data.types.length === 0;
  const coverage = data.summary ? coverageParts(data.summary) : [];
  const noInstances = data.loaded && data.types.length > 0 && !data.types.some(isInstanceBacked);
  const rooms = useMemo(() => roomsOf(data.types, data.summary), [data.types, data.summary]);
  const dele = sel.tview === 'dele';

  /** "+ Tilføj uden geometri": a type with one manual part (in the focused room), then select and name it. */
  function addManual() {
    createGuard.current.run(async () => {
      const created = await addManualType(actions, roomName(rooms, sel.room));
      if (!created) return;
      setSel({ typeId: created.id });
      setRenameId(created.id);
    });
  }

  const partBar = (
    <PartBar
      types={data.types}
      dismissSupported={data.dismissSupported}
      sel={sel}
      setSel={setSel}
      actions={actions}
      busy={data.busy}
      onCreated={setRenameId}
    />
  );

  const stripCount = dele ? filtered.parts.length : stripList.length;
  const view: ModeView = {
    leftPhoneBar: true,
    left: (
      <div className={styles.left}>
        <ModelFilters rooms={rooms} sel={sel} setSel={setSel} filtered={filtered} />
        {coverage.length > 0 && (
          <p className={`${styles.notice} ${styles.desktopOnly}`}>
            <b>Dækning:</b> {coverage.join(' · ')}
          </p>
        )}
        {noInstances && (
          <p className={`${styles.notice} ${styles.warn} ${styles.desktopOnly}`}>
            Ingen instanser — kør Instanser i{' '}
            <TextLink to="/pipeline">Pipeline</TextLink>
            , så typerne får dele fra scanningen.
          </p>
        )}
        {empty && (
          <p className={`${styles.notice} ${styles.desktopOnly}`}>Ingen kortlægning endnu — opret den fra instanserne i panelet til højre.</p>
        )}
      </div>
    ),
    right: empty ? (
      <NoSurvey data={data} actions={actions} />
    ) : type && part ? (
      <PartInspector type={type} part={part} data={data} sel={sel} setSel={setSel} actions={actions} />
    ) : type ? (
      <TypeInspector
        type={type}
        data={data}
        sel={sel}
        setSel={setSel}
        actions={actions}
        onReview={(status) => onReview(type, status)}
        autoRename={renameId === type.id}
        strip={stripList}
        recentPart={lastPart}
      />
    ) : undefined,
    // Type ↔ part (and part ↔ part) replays the right tile's swap-in.
    rightKey: partCode ?? '',
    bottom:
      data.loaded && (stripCount > 0 || data.canEdit || filtersOn) ? (
        <>
          <Strip label={dele ? 'Dele' : 'Typer'} activeKey={dele ? sel.partCode : (type?.id ?? null)}>
            {stripCount === 0 && filtersOn && (
              <p className={styles.stripEmpty}>
                Ingen ressourcer matcher filtrene. <TextLink onClick={() => setSel(NO_FILTER_PATCH)}>Nulstil filtre</TextLink>
              </p>
            )}
            {dele
              ? filtered.parts.map(({ part, type: owner }) => {
                  const mass = partMassText(part);
                  return (
                    <StripCard
                      key={part.code}
                      variant="part"
                      title={owner.name}
                      swatch={lensToken(owner, sel.lens)}
                      figure={formatQuantity(part.quantity, owner.unit)}
                      meta={partLabel(part)}
                      status={mass !== null ? <span className={styles.stripMass}>{mass}</span> : undefined}
                      active={sel.partCode === part.code}
                      onClick={() => setSel({ typeId: owner.id, partCode: part.code, selParts: sel.selParts })}
                    />
                  );
                })
              : stripList.map((t) => (
                  <TypeCard
                    key={t.id}
                    type={t}
                    swatch={lensToken(t, sel.lens)}
                    active={type?.id === t.id}
                    // The open part's own type card goes back up to the type.
                    onSelect={() => setSel(part && t.id === type?.id ? { partCode: null } : { typeId: t.id })}
                    figure={sel.room !== null ? roomFigure(t, sel.room) : undefined}
                  />
                ))}
            {!dele && data.canEdit && <StripCard variant="dashed" title="+ Tilføj uden geometri" disabled={data.busy} onClick={addManual} />}
          </Strip>
          {partBar}
        </>
      ) : undefined,
  };

  const evidence = evidenceCentre(props, type);
  if (evidence) view.centre = evidence;
  else if ((sel.view === 'tabel' || sel.view === 'galleri') && !empty) {
    // Tabel and Galleri list the types themselves, so the strip goes and the tiles reach the foot. The
    // PartBar renders through a portal, so it travels with them instead of keeping an empty bottom slot alive.
    view.centre = (
      <>
        {sel.view === 'galleri' ? (
          <GalleriTile data={data} sel={sel} setSel={setSel} actions={actions} filtered={filtered} onAddManual={addManual} />
        ) : (
          <TabelTile data={data} sel={sel} setSel={setSel} actions={actions} filtered={filtered} />
        )}
        {partBar}
      </>
    );
    view.bottom = undefined;
  }
  return view;
};

/**
 * The Model's filters, atop the left tile (on a phone, one bar under the top
 * bar that scrolls sideways): Typer | Dele (what the strip lists), the
 * shared filters, "Vis omgivelser" while a room is focused (`showOutside`:
 * the rest of the building as a faint ghost instead of hidden), and the
 * set's count ("N typer · M dele"). Its own component, so only it follows
 * the settings.
 */
function ModelFilters({
  rooms,
  sel,
  setSel,
  filtered,
}: Pick<ModeProps, 'sel' | 'setSel'> & { rooms: readonly RoomInfo[]; filtered: FilteredResources }) {
  const storeys = useStoreys();
  // Settings without the Ground's facts (no re-render per published fact); null outside the provider.
  const settings = useGroundSettings();
  return (
    <div className={styles.filters}>
      <TableViewSwitch label="Model" className={styles.tview} value={sel.tview} onChange={(tview) => setSel({ tview })} />
      <ResourceFilters block sel={sel} setSel={setSel} rooms={rooms} storeys={storeys} />
      {sel.room !== null && settings && (
        <ToggleChip
          className={styles.outside}
          pressed={settings.state.showOutside}
          onChange={(showOutside) => settings.dispatch({ type: 'set', patch: { showOutside } })}
          title="Vis resten af bygningen svagt bag rummet"
        >
          Vis omgivelser
        </ToggleChip>
      )}
      <p className={styles.roomLine} aria-live="polite">
        {countText(filtered)}
      </p>
    </div>
  );
}
